/**
 * carrier-filter.ts (T-16, DS-1g-04 §4.5, PIN-1e→1g-CARRIER) — AC-1g-28.
 *
 * Two structural locks keep 1e/1f carrier rows (status changes recorded as
 * `merged_into`/`merge_candidate_into`/`supersedes` on the canonical, per
 * the 1e/1f fold) from ever reaching a PWA list as an item a human can
 * finalize/discard:
 *
 * Lock 1 (no raw-row read). Every server-backed list this batch ships
 * (DecisionLogView/T-18, MasterActionListView/T-19, and DailyLog/T-17)
 * consumes an already-COLLAPSED read — Skill #2's `read_master_list` or
 * Skill #3's `view` fold — typed as `MasterItem[]`/`Decision[]`, neither of
 * which has a carrier-marker field. There is no fetch/read function
 * anywhere in this codebase that reaches a raw `ops.capture` row; LIVE read
 * transport itself is GATED per PIN-1g→1l-PWA-TRANSPORT/S8, so in Stage A
 * this is enforced by construction (no such function exists to call) and
 * verified here by a grep guard over the whole `src/` tree. This module
 * does not implement Lock 1 itself (there is nothing to implement — its
 * proof IS the absence of a code path); it only carries the guard test.
 *
 * Lock 2 (`isTriageable()` single locus). A PWA-EMITTED status change
 * (route-at-save re-route, undo-post-finalize, decision resolve — T-15's
 * `buildCarrierFields`) is modeled locally as a normal outbox entry whose
 * envelope payload carries the `_outbox_kind: "carrier"` marker (the open
 * `payload` bag, §4.7's established locus for non-schema-literal fields).
 * `isTriageable()` is the ONE function anywhere in this codebase that
 * decides whether an outbox record is a first-class, finalizable/
 * discardable list item versus a carrier that may only ever render as an
 * audit line attached to its canonical. Any future release/discard/finalize
 * affordance (DailyLog, T-17) MUST route through `assertTriageable()` —
 * which itself calls `isTriageable()`, never re-implements the check — so
 * there is exactly one locus deciding this, not one per caller.
 */

import type { OutboxRecord } from "../outbox/types";

export const CARRIER_KIND_KEY = "_outbox_kind";
export const CARRIER_KIND_VALUE = "carrier";

/** Stamps the carrier marker onto a payload bag — the ONE way to produce a
 * payload `isTriageable()` will recognize as non-triageable. Does not
 * mutate the input. */
export function markCarrierPayload(payload: Record<string, unknown> = {}): Record<string, unknown> {
  return { ...payload, [CARRIER_KIND_KEY]: CARRIER_KIND_VALUE };
}

/** Lock 2's single locus (AC-1g-28). True for a normal, first-class
 * capture; false for a PWA-emitted carrier (status-change) entry, which
 * may only render as an audit line under its canonical's card and has no
 * finalize/discard affordance. */
export function isTriageable(record: Pick<OutboxRecord, "envelope">): boolean {
  return record.envelope.payload[CARRIER_KIND_KEY] !== CARRIER_KIND_VALUE;
}

export class CarrierNotTriageableError extends Error {
  constructor(capture_id: string) {
    super(
      `carrier-filter: capture_id "${capture_id}" is a kind:carrier entry — ` +
        "it has no finalize/discard affordance (AC-1g-28 Lock 2).",
    );
    this.name = "CarrierNotTriageableError";
  }
}

/** The mandatory guard any release/discard/finalize-style action must call
 * before acting on an outbox record. Throws for a carrier entry rather
 * than returning a boolean the caller could forget to check — enforcing
 * Lock 2 structurally instead of relying on every future caller (T-17's
 * DailyLog, and any later surface) to remember to test `isTriageable()`
 * itself. */
export function assertTriageable(record: Pick<OutboxRecord, "capture_id" | "envelope">): void {
  if (!isTriageable(record)) {
    throw new CarrierNotTriageableError(record.capture_id);
  }
}
